import { isNative } from './platform';

type CameraResult = {
  base64: string;          // base64 (without data: prefix)
  format: 'jpeg' | 'png' | 'webp';
};

export async function pickPhoto(): Promise<CameraResult | null> {
  if (isNative) {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Prompt, // user picks: camera or gallery
      width: 1024,
      height: 1024,
    });
    return {
      base64: photo.base64String!,
      format: (photo.format as 'jpeg' | 'png' | 'webp') ?? 'jpeg',
    };
  }

  // Web fallback: file input
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        const format = file.type.replace('image/', '') as 'jpeg' | 'png' | 'webp';
        resolve({ base64, format });
      };
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

/** Convert a base64 string to a File object (for Supabase upload compatibility) */
export function base64ToFile(base64: string, format: string, filename = 'photo'): File {
  const mimeType = `image/${format}`;
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], `${filename}.${format}`, { type: mimeType });
}
